// Cihaz anahtarı üretimi ve dizine yükleme (9.34 · D-603, D-605).
//
// Uçtan uca şifrelemenin ikinci tuğlası: cihaz kendi anahtarlarını ÜRETİR ve
// AÇIK olanları sunucu dizinine yükler. Üretim saf JS (`crypto.ts`, denetlenmiş
// primitifler); özel anahtarlar cihazın güvenli deposunda (`expo-secure-store`,
// Keychain/Keystore destekli) durur ve buradan HİÇ çıkmaz.
//
// EN SERT KURAL: yukarı yalnız AÇIK materyal + imza gider. Özel anahtarlar
// yalnız cihazın deposuna yazılır (`secureStorage`), gövdeye asla konmaz.
import { getSecret, setSecret, secureStorageAvailable } from './secureStorage';
import {
  generateIdentity,
  generateSignedPreKey,
  generateOneTimePreKeys,
  generateRegistrationId,
  signPreKey,
  x25519PublicKey,
} from './crypto';
import { api, KeyBundleUploadBody, KeyBundleUploadResult } from '../services/api';
import {
  IDENTITY_SECRET as K_ID_SECRET,
  IDENTITY_PUBLIC as K_ID_PUBLIC,
  REGISTRATION_ID as K_REG_ID,
  KEYS_REGISTERED,
  DEVICE_ID as K_DEVICE_ID,
  NEXT_PREKEY_ID,
  signedPreKeySecret,
  oneTimePreKeySecret,
} from './storageKeys';

// Her yeni oturum bir tek-kullanımlık prekey tüketir; 100 makul bir başlangıç
// deposu (sunucu tavanının altında).
const DEFAULT_ONE_TIME_COUNT = 100;

// Cihazın imzalı prekey'i tek ve sabit numaralı. Üç yerde ayrı yazılsaydı yalnız
// şansla aynı kalırlardı; farklılaştıkları an cihaz, elinde olmayan bir anahtarı
// yayınlamış olurdu (9.34 · D-629).
export const SIGNED_PREKEY_ID = 1;

/**
 * Bu ortamda şifreleme kurulabilir mi (9.34 · D-633).
 *
 * Eski hâli doğrudan `expo-secure-store`'a bakıyordu ve HİÇBİR YERDEN
 * çağrılmıyordu. Artık deponun kendisine soruyor ve açılış onu kullanıyor.
 */
export function isSignalAvailable(): boolean {
  return secureStorageAvailable();
}

// Kimlik uzun ömürlüdür: varsa yeniden üretilmez. Yoksa üretilip güvenli depoya
// yazılır, registration id de bir kez üretilip saklanır.
async function getOrCreateIdentity(): Promise<{ publicKey: string; registrationId: number }> {
  const secret = await getSecret(K_ID_SECRET);
  const publicKey = await getSecret(K_ID_PUBLIC);
  const regRaw = await getSecret(K_REG_ID);
  if (secret && publicKey && regRaw) {
    return { publicKey, registrationId: Number(regRaw) };
  }
  const identity = generateIdentity();
  const registrationId = generateRegistrationId();
  await setSecret(K_ID_SECRET, identity.secretKey);
  await setSecret(K_ID_PUBLIC, identity.publicKey);
  await setSecret(K_REG_ID, String(registrationId));
  return { publicKey: identity.publicKey, registrationId };
}

/**
 * Cihaz anahtarlarını üret ve dizine yükle. Kimlik korunur; imzalı prekey ve bir
 * demet tek-kullanımlık prekey üretilir, özel yanları cihaz deposuna yazılır,
 * açık yanları `PUT /keys`'e gönderilir. Dönen değer sunucudaki güncel
 * tek-kullanımlık prekey sayısıdır.
 */
export async function registerDeviceKeys(
  token: string,
  opts: { signedPreKeyId?: number; oneTimeStartId?: number; oneTimeCount?: number } = {},
): Promise<KeyBundleUploadResult> {
  const identity = await getOrCreateIdentity();
  const identitySecret = await getSecret(K_ID_SECRET);
  if (!identitySecret) {
    throw new Error('Kimlik özel anahtarı yok');
  }

  const signedPreKeyId = opts.signedPreKeyId ?? SIGNED_PREKEY_ID;
  const signed = generateSignedPreKey(signedPreKeyId, identitySecret);
  await setSecret(signedPreKeySecret(signed.id), signed.secretKey);

  const startId = opts.oneTimeStartId ?? 1;
  const count = opts.oneTimeCount ?? DEFAULT_ONE_TIME_COUNT;
  const oneTime = generateOneTimePreKeys(startId, count);
  for (const otp of oneTime) {
    await setSecret(oneTimePreKeySecret(otp.id), otp.secretKey);
  }

  const body: KeyBundleUploadBody = {
    registration_id: identity.registrationId,
    identity_key: identity.publicKey,
    signed_prekey: { id: signed.id, key: signed.publicKey, signature: signed.signature },
    one_time_prekeys: oneTime.map((o) => ({ id: o.id, key: o.publicKey })),
  };

  return api.uploadKeyBundle(token, body);
}

// Sunucudaki tek-kullanımlık prekey deposu bunun altına düşerse tazelenir. Her
// yeni oturum bir tanesini tüketiyor; depo biterse oturumlar kurulmaya devam
// eder ama ileriye dönük gizlilik bir nebze zayıflar (X3DH'nin DH4'ü düşer).
const REPLENISH_THRESHOLD = 20;

/**
 * Cihaz anahtarlarını BİR KEZ yayımla (9.34 · D-613). Giriş yapmış her açılışta
 * çağrılabilir; ikinci çağrıda hiçbir şey yapmaz.
 *
 * Neden bayrak şart: ikinci bir tam kayıt, tek-kullanımlık prekey'leri aynı
 * kimliklerle yeniden üretirdi. Sunucu `ON CONFLICT DO NOTHING` ile ESKİ açık
 * anahtarları tutar, cihaz ise YENİ özelleri yazardı; sunucunun dağıttığı
 * anahtar cihazdakiyle uyuşmaz ve her oturum kurulumu sessizce çökerdi.
 */
export async function ensureDeviceKeys(token: string): Promise<boolean> {
  if (await getSecret(KEYS_REGISTERED)) return false;

  const count = DEFAULT_ONE_TIME_COUNT;
  const sonuc = await registerDeviceKeys(token, {
    signedPreKeyId: SIGNED_PREKEY_ID, oneTimeStartId: 1, oneTimeCount: count,
  });
  // Sunucunun bu cihaza ayırdığı numara (D-618); sayım isteği bununla soruluyor.
  await setSecret(K_DEVICE_ID, String(sonuc.device_id));
  // Sıradaki kimlik kullanılanların HEMEN ardından başlar; 1..count bir daha
  // asla üretilmez.
  await setSecret(NEXT_PREKEY_ID, String(1 + count));
  await setSecret(KEYS_REGISTERED, '1');
  return true;
}

/**
 * Sunucudaki tek-kullanımlık prekey deposu azaldıysa taze anahtar yükle.
 * Kimlikler kaldığı yerden devam eder — tekrar eden kimlik dizini bozardı.
 * Kayıt yapılmamışsa hiçbir şey yapmaz.
 */
export async function replenishOneTimePreKeys(
  token: string,
  opts: { threshold?: number; count?: number } = {},
): Promise<number> {
  if (!(await getSecret(KEYS_REGISTERED))) return 0;

  const kayitliCihaz = Number(await getSecret(K_DEVICE_ID)) || undefined;
  const { one_time_prekeys: remaining } = await api.keyBundleCount(token, kayitliCihaz);
  if (remaining > (opts.threshold ?? REPLENISH_THRESHOLD)) return 0;

  const identitySecret = await getSecret(K_ID_SECRET);
  const identityPublic = await getSecret(K_ID_PUBLIC);
  const regRaw = await getSecret(K_REG_ID);
  if (!identitySecret || !identityPublic || !regRaw) return 0;

  const startId = Number(await getSecret(NEXT_PREKEY_ID)) || 1;
  const count = opts.count ?? DEFAULT_ONE_TIME_COUNT;
  const fresh = generateOneTimePreKeys(startId, count);
  for (const otp of fresh) {
    await setSecret(oneTimePreKeySecret(otp.id), otp.secretKey);
  }

  // İmzalı prekey DEĞİŞTİRİLMİYOR: onu döndürmek, henüz kurulmamış oturumların
  // dayandığı özeli ezerdi. Yalnız tek-kullanımlık demet ekleniyor.
  const signedPreKeyId = SIGNED_PREKEY_ID;
  const signedSecret = await getSecret(signedPreKeySecret(signedPreKeyId));
  if (!signedSecret) return 0;
  const signedPublic = x25519PublicKey(signedSecret);

  const yenile = await api.uploadKeyBundle(token, {
    registration_id: Number(regRaw),
    identity_key: identityPublic,
    signed_prekey: {
      id: signedPreKeyId,
      key: signedPublic,
      signature: signPreKey(identitySecret, signedPublic),
    },
    one_time_prekeys: fresh.map((o) => ({ id: o.id, key: o.publicKey })),
  });
  // Numara kimlik anahtarından çözüldüğü için hep aynısı döner; yine de
  // saklanıyor ki ilk kaydı bu sürümden önce yapılmış cihazlar da öğrensin.
  await setSecret(K_DEVICE_ID, String(yenile.device_id));
  await setSecret(NEXT_PREKEY_ID, String(startId + count));
  return fresh.length;
}

/**
 * Cihazın dizindeki kaydını her açılışta tazele (9.34 · D-629).
 *
 * İKİ İŞ, TEK ÇAĞRI — ikisi de "cihaz, dizindeki kendi kaydından sorumludur"
 * kuralının parçası.
 *
 * BİRİNCİSİ, ÖLÇÜLEN KUSUR: cihaz KENDİ NUMARASINI öğrenir. Numarayı sunucu
 * veriyor (D-618) ve cihaz onu uyduramaz. D-618'den eski bir kurulumun numarayı
 * öğrenecek hiçbir yolu yoktu; numarasını bilmeyen cihaz mesajları numarasız
 * istiyor, sunucu da ona kendi zarfını değil eski alanı veriyor — o alan KARŞI
 * TARAFIN son cihazına adreslenmiş. Açamaması normaldi. Gönderme bu süre boyunca
 * çalışmaya devam ettiği için hiçbir şey bozuk görünmüyordu.
 *
 * İKİNCİSİ, ÖNLEM: yayınlanan imzalı prekey, cihazın GERÇEKTEN açabildiği anahtar
 * olsun.
 *
 * KUSUR. Bir cihaz, dizine kendi elinde olmayan bir imzalı prekey yayınlamış
 * olabiliyor: güvenli deponun binary'de bulunmadığı dönemde (D-616) anahtarlar
 * üretilip yüklendi ama özelleri yazılamadı. O cihaz mesaj GÖNDEREBİLİYOR —
 * gönderme yalnız kimlik özelini ister — ama kendisine kurulan her yeni oturum
 * sessizce çöküyor: karşı taraf yayınlanan imzalı prekey'e göre sır üretiyor,
 * cihazın elindeki farklı. Ekranda tek iz "Şifreli mesaj" olduğu için günlerce
 * sürebiliyor. D-620 o dönemin TEK-KULLANIMLIK anahtarlarını temizlemişti;
 * imzalı olan, dizinin tam ortasında duruyordu.
 *
 * KURAL. Bunu sunucu bilemez — elindeki yalnız açık anahtar, ona bakarak
 * karşılığının cihazda durup durmadığı anlaşılmaz. Yalnız cihaz bilebilir. O
 * yüzden yükümlülük cihazda: her açılışta, ELİNDEKİ özelden türettiği açık
 * anahtarı yayınlar. Özel duruyorsa yayın aynı kalır (işlem etkisiz); özel
 * yoksa yeni bir çift üretilir ve dizin onunla düzeltilir. Böylece "yayınladığı
 * anahtarı kullanabilmek" bir denetim değil, yapının kendisi olur.
 *
 * Özel duruyorken anahtar DÖNDÜRÜLMÜYOR: henüz açılmamış gelen oturumlar ona
 * dayanıyor olabilir, döndürmek onları kırardı. Tek-kullanımlık demet de boş
 * gidiyor; bu çağrının işi depo doldurmak değil.
 */
export async function refreshDirectoryEntry(
  token: string,
): Promise<'kayit-yok' | 'dogrulandi' | 'yenilendi'> {
  if (!(await getSecret(KEYS_REGISTERED))) return 'kayit-yok';

  const identitySecret = await getSecret(K_ID_SECRET);
  const identityPublic = await getSecret(K_ID_PUBLIC);
  const regRaw = await getSecret(K_REG_ID);
  if (!identitySecret || !identityPublic || !regRaw) return 'kayit-yok';

  let secret = await getSecret(signedPreKeySecret(SIGNED_PREKEY_ID));
  let durum: 'dogrulandi' | 'yenilendi' = 'dogrulandi';
  if (!secret) {
    const taze = generateSignedPreKey(SIGNED_PREKEY_ID, identitySecret);
    await setSecret(signedPreKeySecret(taze.id), taze.secretKey);
    secret = taze.secretKey;
    durum = 'yenilendi';
  }

  const publicKey = x25519PublicKey(secret);
  const sonuc = await api.uploadKeyBundle(token, {
    registration_id: Number(regRaw),
    identity_key: identityPublic,
    signed_prekey: {
      id: SIGNED_PREKEY_ID,
      key: publicKey,
      signature: signPreKey(identitySecret, publicKey),
    },
    one_time_prekeys: [],
  });
  await setSecret(K_DEVICE_ID, String(sonuc.device_id));
  return durum;
}
